import { AddressDialog } from '@/components/AddressDialog';
import { PermissionGuard } from '@/components/PermissionGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useGetAddresses } from '@/hooks/useAddress';
import { useDeleteAddress } from '@/hooks/useAddressActions';
import type { Address } from '@octave/contracts';
import { AlertTriangle, Loader2, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface AddressesSectionProps {
  organizationId: string | undefined;
}

export function AddressesSection({ organizationId }: AddressesSectionProps) {
  const { data: addresses, isLoading } = useGetAddresses(organizationId);
  const { mutate: deleteAddress } = useDeleteAddress();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);

  const handleConfirmDelete = () => {
    if (addressToDelete) {
      deleteAddress(addressToDelete.id, {
        onSuccess: () => setAddressToDelete(null),
      });
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-slate-900">Adresses de livraison</h2>
        <PermissionGuard permission="write">
          <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Ajouter
          </Button>
        </PermissionGuard>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : addresses && addresses.length === 0 ? (
        <p className="text-sm text-slate-500 py-4">
          Aucune adresse enregistrée
        </p>
      ) : (
        <div className="space-y-2">
          {addresses?.map((address) => (
            <div
              key={address.id}
              className="bg-white rounded-xl px-5 py-4 hover:shadow-md transition-all flex items-center gap-4"
            >
              {/* Icon */}
              <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <MapPin className="h-4.5 w-4.5 text-emerald-700" />
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {address.name}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {address.street}, {address.zipCode} {address.city}, {address.country}
                </p>
                {(address.companyName || address.recipientName) && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">
                    {[address.companyName, address.recipientName].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>

              {/* Type badge */}
              <Badge
                variant="outline"
                className={
                  address.isPersonal
                    ? 'text-xs bg-violet-50 text-violet-600 border-violet-200 shrink-0'
                    : 'text-xs bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0'
                }
              >
                {address.isPersonal ? 'Personnelle' : 'Professionnelle'}
              </Badge>

              {/* Actions */}
              <PermissionGuard permission="write">
                <div className="flex items-center gap-0.5 shrink-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-slate-700"
                        onClick={() => setSelectedAddress(address)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Modifier</TooltipContent>
                  </Tooltip>
                  <PermissionGuard permission="delete">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          onClick={() => setAddressToDelete(address)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Supprimer</TooltipContent>
                    </Tooltip>
                  </PermissionGuard>
                </div>
              </PermissionGuard>
            </div>
          ))}
        </div>
      )}

      <AddressDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      {selectedAddress && (
        <AddressDialog
          open={!!selectedAddress}
          onOpenChange={(open) => !open && setSelectedAddress(null)}
          address={selectedAddress}
        />
      )}

      <Dialog
        open={!!addressToDelete}
        onOpenChange={(open) => !open && setAddressToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Supprimer cette adresse ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          {addressToDelete && (
            <div className="py-2 space-y-0.5">
              <p className="font-medium text-sm">{addressToDelete.name}</p>
              <p className="text-sm text-muted-foreground">
                {addressToDelete.street}
              </p>
              <p className="text-sm text-muted-foreground">
                {addressToDelete.zipCode} {addressToDelete.city},{' '}
                {addressToDelete.country}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressToDelete(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
